import type { NetworkTopicSettingsClients_settings } from '@ms/yammer-graphql';
import queryNetworkTopicSettings from '@ms/yammer-graphql/dist/operations/queryNetworkTopicSettings.graphql';
import { reportError } from '@ms/yammer-telemetry';
import type { RequestInitiator } from '@ms/yammer-telemetry-support';

import type { ManagedApiCaching, ManagedApiLifecycle } from '../..';
import { getGraphqlApiRequest, managedApiAction } from '../..';
import type { PromiseThunkAction } from '../../types';

import type {
  NetworkTopicSettingsFulfilledAction,
  NetworkTopicSettingsPendingAction,
  NetworkTopicSettingsRejectedAction,
  NetworkTopicSettingsResponse,
} from './actions.types';
import { getCacheKey } from './cacheKey';

const loadNetworkTopicSettingsPending = (): NetworkTopicSettingsPendingAction => ({
  type: 'NETWORK_TOPIC_SETTINGS.LOAD_PENDING',
});

const loadNetworkTopicSettingsFulfilled = (
  networkTopicSettings: NetworkTopicSettingsClients_settings
): NetworkTopicSettingsFulfilledAction => ({
  type: 'NETWORK_TOPIC_SETTINGS.LOAD_FULFILLED',
  payload: {
    networkTopicSettings,
  },
});

const loadNetworkTopicSettingsRejected = (error: unknown): NetworkTopicSettingsRejectedAction => ({
  type: 'NETWORK_TOPIC_SETTINGS.LOAD_REJECTED',
  payload: {
    error,
  },
  error: true,
});

const loadNetworkTopicSettingsRequest = (initiator?: RequestInitiator) =>
  getGraphqlApiRequest({
    body: {
      operationName: 'NetworkTopicSettingsClients',
      query: queryNetworkTopicSettings,
    },
    initiator,
  });

const loadNetworkTopicSettingsLifecycle = (): ManagedApiLifecycle<NetworkTopicSettingsResponse> => ({
  pending: () => loadNetworkTopicSettingsPending(),
  fulfilled: (response) => loadNetworkTopicSettingsFulfilled(response.data.settings),
  rejected: (error) => loadNetworkTopicSettingsRejected(error),
});

interface LoadNetworkTopicSettingsOptions {
  readonly caching?: ManagedApiCaching;
  readonly initiator?: RequestInitiator;
}

type LoadNetworkTopicSettings = (options: LoadNetworkTopicSettingsOptions) => PromiseThunkAction<void>;
export const loadNetworkTopicSettings: LoadNetworkTopicSettings =
  ({ caching, initiator }) =>
  async (dispatch) => {
    const request = loadNetworkTopicSettingsRequest(initiator);
    const lifecycle = loadNetworkTopicSettingsLifecycle();
    const cachingWithKey = caching && {
      ...caching,
      key: getCacheKey(),
    };
    const action = managedApiAction({ request, lifecycle, caching: cachingWithKey });

    try {
      await dispatch(action);
    } catch (error) {
      reportError({ error });
    }
  };
